import { useState, useMemo } from "react";
import { RefreshCw, Eye, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { useAuth } from "@/utils/useAuth";
import { cn } from "@/lib/utils";

const cfBlue = '#0084FF';
const cfGreen = '#10B981';
const cfOrange = '#FFB84D';

interface MetricCardProps {
  label: string;
  value: number;
  change: number;
  sparklineData: number[];
}

function MetricCard({ label, value, change, sparklineData }: MetricCardProps) {
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-lg p-4">
      <div className="text-xs font-semibold text-[#64748b] uppercase mb-2">
        {label}
      </div>
      <div className="text-3xl font-bold text-[#1e293b] mb-1">
        {value.toLocaleString()}
      </div>
      <div className={cn(
        "text-xs font-semibold mb-3",
        change >= 0 ? "text-[#0084FF]" : "text-[#10B981]"
      )}>
        {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
      </div>
      <div style={{ height: '40px', marginTop: '8px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparklineData.map((v, i) => ({ value: v }))}>
            <Line type="monotone" dataKey="value" stroke={cfBlue} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ModernAnalyticsDashboard() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Data queries
  const { data: stats, isLoading: statsLoading } = trpc.dashboard.getStats.useQuery();
  const { data: requestsByStatus, isLoading: statusLoading } = trpc.dashboard.getRequestsByStatus.useQuery();
  const { data: requestsByType, isLoading: typeLoading } = trpc.dashboard.getRequestsByType.useQuery();
  const { data: dailyTrend, isLoading: trendLoading } = trpc.dashboard.getDailyTrend.useQuery();
  const { data: recentActivity, isLoading: activityLoading } = trpc.dashboard.getRecentActivity.useQuery();
  const { data: pendingItems, isLoading: pendingLoading } = trpc.dashboard.getPendingItems.useQuery();
  const utils = trpc.useUtils();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        utils.dashboard.getStats.invalidate(),
        utils.dashboard.getRequestsByStatus.invalidate(),
        utils.dashboard.getRequestsByType.invalidate(),
        utils.dashboard.getDailyTrend.invalidate(),
        utils.dashboard.getRecentActivity.invalidate(),
        utils.dashboard.getPendingItems.invalidate(),
      ]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const lastUpdated = format(new Date(), "dd MMM yyyy, HH:mm");

  // Sparkline data (in production, use real data)
  const sparklineData = [
    { value: 32 }, { value: 45 }, { value: 28 }, { value: 51 }, { value: 42 }, { value: 18 }, { value: 52 }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#e5e7eb] px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1e293b]">Modern Analytics Dashboard</h2>
          <p className="text-xs text-[#64748b] mt-1">Request Processing Performance Metrics</p>
        </div>
        <div className="flex gap-3">
          <select className="px-3 py-2 border border-[#e5e7eb] rounded-md text-sm text-[#64748b] bg-white">
            <option>All deployed versions</option>
          </select>
          <select className="px-3 py-2 border border-[#e5e7eb] rounded-md text-sm text-[#64748b] bg-white">
            <option>Last 24 hours UTC</option>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#e5e7eb] px-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-transparent border-b-0 gap-0">
            <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0084FF] data-[state=active]:text-[#0084FF] px-4 py-3 text-xs font-semibold uppercase">
              Overview
            </TabsTrigger>
            <TabsTrigger value="metrics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0084FF] data-[state=active]:text-[#0084FF] px-4 py-3 text-xs font-semibold uppercase">
              Metrics
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0084FF] data-[state=active]:text-[#0084FF] px-4 py-3 text-xs font-semibold uppercase">
              Settings
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* KPI Cards Grid */}
        <div className="grid grid-cols-6 gap-4 mb-8">
          <MetricCard
            label="Total Requests"
            value={stats?.totalRequestsThisMonth || 0}
            change={23.5}
            sparklineData={sparklineData}
          />
          <MetricCard
            label="Pending Review"
            value={stats?.pendingApprovals || 0}
            change={12.3}
            sparklineData={sparklineData}
          />
          <MetricCard
            label="Approved"
            value={stats?.approvedCount || 0}
            change={8.7}
            sparklineData={sparklineData}
          />
          <MetricCard
            label="Rejected"
            value={stats?.rejectedCount || 0}
            change={-5.2}
            sparklineData={sparklineData}
          />
          <MetricCard
            label="Avg Response Time"
            value={2.1}
            change={-15.4}
            sparklineData={sparklineData}
          />
          <MetricCard
            label="Success Rate"
            value={91}
            change={2.1}
            sparklineData={sparklineData}
          />
        </div>

        {/* Active Deployments Section */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-[#1e293b] mb-4">Active Deployments</h3>
          <div className="bg-white border border-[#e5e7eb] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e5e7eb]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Version ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Deployed</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Traffic %</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Requests/sec</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Error Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase">Approval Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#e5e7eb] hover:bg-[#f8fafc]">
                  <td className="px-4 py-3 text-sm font-mono text-[#64748b]"><code className="bg-[#f0f2f5] px-2 py-1 rounded">v1.2.3</code></td>
                  <td className="px-4 py-3 text-sm text-[#475569]">12 hours ago</td>
                  <td className="px-4 py-3">
                    <div className="w-40">
                      <div className="h-1.5 bg-[#e5e7eb] rounded overflow-hidden mb-1">
                        <div className="h-full bg-[#0084FF]" style={{ width: '100%' }}></div>
                      </div>
                      <div className="text-xs text-[#64748b]">100%</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#475569]">42.5 req/sec</td>
                  <td className="px-4 py-3 text-sm text-[#475569]">0%</td>
                  <td className="px-4 py-3 text-sm font-medium text-[#475569]">91%</td>
                </tr>
                <tr className="border-b border-[#e5e7eb] hover:bg-[#f8fafc]">
                  <td className="px-4 py-3 text-sm font-mono text-[#64748b]"><code className="bg-[#f0f2f5] px-2 py-1 rounded">v1.2.2</code></td>
                  <td className="px-4 py-3 text-sm text-[#475569]">3 days ago</td>
                  <td className="px-4 py-3">
                    <div className="w-40">
                      <div className="h-1.5 bg-[#e5e7eb] rounded overflow-hidden mb-1">
                        <div className="h-full bg-[#0084FF]" style={{ width: '0%' }}></div>
                      </div>
                      <div className="text-xs text-[#64748b]">0%</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#475569]">0 req/sec</td>
                  <td className="px-4 py-3 text-sm text-[#475569]">0%</td>
                  <td className="px-4 py-3 text-sm font-medium text-[#475569]">88%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Request Trend */}
          <div className="bg-white border border-[#e5e7eb] rounded-lg p-6">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Request Trend</h3>
            {trendLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke={cfBlue} name="Submitted" strokeWidth={2} />
                  <Line type="monotone" dataKey="approved" stroke={cfGreen} name="Approved" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Status Distribution */}
          <div className="bg-white border border-[#e5e7eb] rounded-lg p-6">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Status Distribution</h3>
            {statusLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={requestsByStatus || []} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={80} label>
                    {requestsByStatus?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
